import { useState } from "react";
import { api, formatApiError } from "@/lib/kfmApi";
import { useTrackedLinks, trackedIntent, trackedCopyLink, trackedCaption } from "@/lib/utm";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Share2, Copy, Check, Loader2, Twitter, Facebook, MessageCircle, Mail, Linkedin, Instagram, FileDown } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CAMPAIGN = "teach_share";

// Share a finished Teach & Share draft: public link + PDF + direct-to-social.
export default function ShareTeachDialog({ sessionId, testid = "teach-share" }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [share, setShare] = useState(null); // { hook, url, read_url, pdf_url, token }
  const [copied, setCopied] = useState(false);

  const ensureShare = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/study/sessions/${sessionId}/teach-share/share`);
      if (data.shared) setShare(data);
      else setShare((await api.post(`/study/sessions/${sessionId}/teach-share/share`)).data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create a share link");
    }
    setLoading(false);
  };

  const onOpen = () => { setOpen(true); if (!share) ensureShare(); };

  const link = share?.url || "";
  const hook = share?.hook || "";
  const tracked = useTrackedLinks(link, CAMPAIGN);
  const copyTarget = trackedCopyLink(tracked, link, CAMPAIGN);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(copyTarget);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
      toast.success("Link copied");
    } catch { toast.error("Couldn't copy — select the link and copy it manually"); }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "A teaching draft on OpenWord AI", text: hook, url: copyTarget }); } catch { /* dismissed */ }
    } else copy();
  };

  const copyInstagram = async () => {
    try {
      await navigator.clipboard.writeText(trackedCaption(tracked, link, hook, CAMPAIGN));
      toast.success("Caption + link copied — paste it into your Instagram post or story");
    } catch { toast.error("Couldn't copy the caption"); }
  };

  const targets = [
    { id: "facebook", label: "Share on Facebook", Icon: Facebook, href: trackedIntent("fb", tracked, link, hook, CAMPAIGN) },
    { id: "x", label: "Share on X", Icon: Twitter, href: trackedIntent("x", tracked, link, hook, CAMPAIGN) },
    { id: "whatsapp", label: "Share on WhatsApp", Icon: MessageCircle, href: trackedIntent("wa", tracked, link, hook, CAMPAIGN) },
    { id: "linkedin", label: "Share on LinkedIn", Icon: Linkedin, href: trackedIntent("li", tracked, link, hook, CAMPAIGN) },
  ];

  const unshare = async () => {
    try {
      await api.delete(`/study/sessions/${sessionId}/teach-share/share`);
      setShare(null); setOpen(false);
      toast.success("This draft is no longer shared");
    } catch { toast.error("Couldn't stop sharing"); }
  };

  const pdfHref = `${BACKEND}/api/study/sessions/${sessionId}/teach-share/pdf`;

  return (
    <>
      <button data-testid={`${testid}-open`} onClick={onOpen}
              className="h-9 px-4 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm font-medium inline-flex items-center gap-1.5 transition-colors">
        <Share2 className="w-4 h-4" strokeWidth={1.5} /> Share &amp; export
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${testid}-dialog`} className="bg-[#0E131F] sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl text-white">Share this draft</DialogTitle>
            <DialogDescription className="text-sm text-slate-400">
              Share a public read-only link, download a PDF, or post it directly to social.
            </DialogDescription>
          </DialogHeader>

          {/* PDF works whether or not a public link exists */}
          <a data-testid={`${testid}-pdf`} href={pdfHref} target="_blank" rel="noreferrer"
             className="w-full h-11 rounded-xl border border-[#29344D] text-slate-300 text-sm font-medium flex items-center justify-center gap-2 hover:border-[#00E5FF]/50 transition-colors">
            <FileDown className="w-4 h-4" strokeWidth={1.5} /> Download PDF
          </a>

          {loading || !share ? (
            <div className="py-10 flex flex-col items-center gap-3 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin text-[#00E5FF]" strokeWidth={1.5} />
              <span className="text-sm">Preparing a shareable link…</span>
            </div>
          ) : (
            <div className="space-y-5">
              <div data-testid={`${testid}-hook`} className="rounded-xl bg-[#10222A] border border-[#00E5FF]/15 p-4 text-sm text-slate-300 leading-relaxed italic">
                {hook}
              </div>

              <div className="flex items-center gap-2">
                <input data-testid={`${testid}-link`} readOnly value={link} onFocus={(e) => e.target.select()}
                       className="flex-1 h-11 rounded-xl border border-[#29344D] bg-[#0B0F16] px-3 text-sm text-slate-400 truncate focus:outline-none" />
                <button data-testid={`${testid}-copy`} onClick={copy} title="Copy link"
                        className="h-11 w-11 shrink-0 rounded-xl bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] flex items-center justify-center transition-colors">
                  {copied ? <Check className="w-5 h-5" strokeWidth={1.5} /> : <Copy className="w-5 h-5" strokeWidth={1.5} />}
                </button>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button data-testid={`${testid}-native`} onClick={nativeShare}
                        className="flex-1 min-w-[120px] h-11 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm font-medium flex items-center justify-center gap-2 transition-colors">
                  <Share2 className="w-4 h-4" strokeWidth={1.5} /> Share…
                </button>
                {targets.map((t) => (
                  <a key={t.id} data-testid={`${testid}-${t.id}`} href={t.href} target="_blank" rel="noreferrer"
                     aria-label={t.label} title={t.label}
                     className="w-11 h-11 shrink-0 flex items-center justify-center rounded-full bg-[#10222A] text-[#00E5FF] hover:bg-[#10222A] transition-colors">
                    <t.Icon className="w-4 h-4" strokeWidth={1.5} />
                  </a>
                ))}
                <button data-testid={`${testid}-instagram`} onClick={copyInstagram} aria-label="Copy caption for Instagram"
                        title="Instagram has no direct web share — copy the caption + link to paste"
                        className="w-11 h-11 shrink-0 flex items-center justify-center rounded-full bg-[#10222A] text-[#00E5FF] hover:bg-[#10222A] transition-colors">
                  <Instagram className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <a data-testid={`${testid}-email`} href={`mailto:?subject=${encodeURIComponent("A teaching draft on OpenWord AI")}&body=${encodeURIComponent(hook + "\n\n" + copyTarget)}`}
                   aria-label="Send by email" title="Send by email"
                   className="w-11 h-11 shrink-0 flex items-center justify-center rounded-full bg-[#10222A] text-[#00E5FF] hover:bg-[#10222A] transition-colors">
                  <Mail className="w-4 h-4" strokeWidth={1.5} />
                </a>
              </div>

              <button data-testid={`${testid}-unshare`} onClick={unshare}
                      className="w-full text-sm text-slate-500 hover:text-red-500 transition-colors">
                Stop sharing this draft
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
